import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RestService } from 'src/app/services/rest.service';
import { SMADto } from 'src/app/models/sma';
import { ResponseDto } from 'src/app/models/dto/response';

export interface DialogAddSmaData {
  licenceGuid: string;
  licenceId: number;
  saveLicenceFirst?: boolean; // Flag to indicate if licence needs to be saved first
  licenceData?: any; // The licence data to save
  smaGuid?: string; // If editing existing SMA
}

@Component({
  selector: 'app-dialog-add-sma',
  templateUrl: './dialog-add-sma.component.html',
  styleUrl: './dialog-add-sma.component.scss'
})
export class DialogAddSmaComponent implements OnInit {
  form: FormGroup;
  isEditMode: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DialogAddSmaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogAddSmaData,
    private restService: RestService
  ) {
    this.form = new FormGroup({
      start_date: new FormControl('', [Validators.required]),
      end_date: new FormControl('', [Validators.required])
    });

    this.isEditMode = !!this.data.smaGuid;
  }

  ngOnInit(): void {
    // Don't load existing SMA data if this is a new licence that needs to be saved first
    if (this.data.saveLicenceFirst) {
      console.log('Skipping SMA data load - licence needs to be saved first');
      return;
    }

    // Load existing SMA data if editing
    if (this.isEditMode && this.data.smaGuid) {
      this.loadSmaData(this.data.smaGuid);
    } else if (this.data.licenceId && this.data.licenceId > 0) {
      // Load SMA data for the licence
      this.loadSmaDataByLicence(this.data.licenceId);
    }
  }

  loadSmaDataByLicence(licenceId: number): void {
    this.restService.get<any>(`/licences/period?licenceId=${licenceId}`).subscribe(
      (response) => {
        if (response && response.data) {
          // If there's existing SMA data, populate the form
          const smaData = Array.isArray(response.data) ? response.data[0] : response.data;
          if (smaData) {
            this.populateForm(smaData);
            this.isEditMode = true;
          }
        }
      },
      (error) => {
        console.log('No existing SMA data found or error loading:', error);
      }
    );
  }

  loadSmaData(guid: string): void {
    // If we need to load by GUID instead
    this.restService.get<any>(`/licences/period/${guid}`).subscribe(
      (response) => {
        if (response && response.data) {
          this.populateForm(response.data);
        }
      },
      (error) => {
        console.error('Error loading SMA data:', error);
      }
    );
  }

  populateForm(smaData: any): void {
    // Extract date from ISO string (YYYY-MM-DD)
    const startDate = smaData.StartDate || smaData.start_date;
    const endDate = smaData.EndDate || smaData.end_date;

    this.form.patchValue({
      start_date: startDate ? startDate.split('T')[0] : '',
      end_date: endDate ? endDate.split('T')[0] : ''
    });
  }

  save() {
    if (this.form.valid) {
      const smaDto: SMADto = {
        LicenceID: this.data.licenceId,
        StartDate: this.form.get('start_date')?.value + 'T00:00:00Z',
        EndDate: this.form.get('end_date')?.value + 'T00:00:00Z',
      };

      // If licence needs to be saved first, just return the data without saving
      if (this.data.saveLicenceFirst) {
        console.log('Returning SMA data without saving (licence will be saved first):', smaDto);
        this.dialogRef.close({ sma: smaDto, saveLicenceFirst: true, success: true });
        return;
      }

      // Use PUT if editing, POST if creating (for existing licences)
      const request = this.isEditMode && this.data.smaGuid
        ? this.restService.put<ResponseDto>(`/licences/period/${this.data.smaGuid}`, smaDto)
        : this.restService.post<ResponseDto>('/licences/period', smaDto);

      request.subscribe(
        (response) => {
          console.log('SMA saved successfully:', response);
          this.dialogRef.close({ sma: smaDto, saveLicenceFirst: this.data.saveLicenceFirst, success: true });
        },
        (error) => {
          console.error('Error saving SMA:', error);
          // Still close with data so parent can handle it
          this.dialogRef.close({ sma: smaDto, saveLicenceFirst: this.data.saveLicenceFirst, success: false, error });
        }
      );
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
